/**
 * Canonical V2 SPA analytics path — browser pathname stays `/app-beta`
 * while tabs are virtual screens. Browser-only module state (never on server).
 */

import {
  sanitizeAnalyticsLocation,
  sanitizeAnalyticsPath,
} from '@/lib/analytics/sanitizeAnalyticsPath'

export const WAGAMOUR_V2_SCREENS = [
  'home',
  'parks',
  'play',
  'picks',
  'profile',
] as const

export type WagamourV2Screen = (typeof WAGAMOUR_V2_SCREENS)[number]

const V2_SCREEN_SET = new Set<string>(WAGAMOUR_V2_SCREENS)

/** Most recently tracked V2 screen path, e.g. `/app-beta/play`. */
let currentWagamourAnalyticsPath: string | null = null

export function isAppBetaShellPath(
  pathname: string | null | undefined
): boolean {
  return sanitizeAnalyticsPath(pathname) === '/app-beta'
}

export function virtualWagamourScreenPath(screen: WagamourV2Screen): string {
  return `/app-beta/${screen}`
}

/** Record the active V2 screen. No-op on the server (no cross-request leak). */
export function setCurrentWagamourAnalyticsScreen(
  screen: WagamourV2Screen
): string {
  const path = virtualWagamourScreenPath(screen)
  if (typeof window === 'undefined') return path
  currentWagamourAnalyticsPath = path
  return path
}

export function getCurrentWagamourAnalyticsPath(): string | null {
  if (typeof window === 'undefined') return null
  return currentWagamourAnalyticsPath
}

/**
 * True when analytics does not need a V2 virtual screen, or one is already set.
 * On `/app-beta` the shell path is not enough — a tracked screen must exist.
 */
export function isWagamourScreenContextReady(
  pathname?: string | null
): boolean {
  if (typeof window === 'undefined') return true
  const real = sanitizeAnalyticsPath(
    pathname !== undefined && pathname !== null
      ? pathname
      : window.location.pathname || ''
  )
  if (real !== '/app-beta') return true
  return currentWagamourAnalyticsPath != null
}

/**
 * Bounded poll until a canonical V2 screen exists on `/app-beta`, or until
 * the path is not the app shell. Resolves false on timeout/abort while still
 * on `/app-beta` with no screen — callers may then fall back to the shell path.
 */
export function whenWagamourScreenContextReady(options?: {
  timeoutMs?: number
  intervalMs?: number
  signal?: AbortSignal
}): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 12_000
  const intervalMs = options?.intervalMs ?? 50
  const signal = options?.signal

  if (isWagamourScreenContextReady()) return Promise.resolve(true)
  if (typeof window === 'undefined') return Promise.resolve(true)
  if (signal?.aborted) return Promise.resolve(false)
  if (timeoutMs <= 0) return Promise.resolve(false)

  return new Promise((resolve) => {
    const start = Date.now()
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const finish = (ok: boolean) => {
      if (timeoutId !== undefined) clearTimeout(timeoutId)
      signal?.removeEventListener('abort', onAbort)
      resolve(ok)
    }

    const onAbort = () => finish(false)

    signal?.addEventListener('abort', onAbort, { once: true })

    const tick = () => {
      if (signal?.aborted) {
        finish(false)
        return
      }
      if (isWagamourScreenContextReady()) {
        finish(true)
        return
      }
      if (Date.now() - start >= timeoutMs) {
        finish(false)
        return
      }
      timeoutId = setTimeout(tick, intervalMs)
    }

    tick()
  })
}

/**
 * Analytics page_path for the current document.
 * On `/app-beta`, uses the last tracked V2 screen when one exists.
 * Leaving `/app-beta` clears the virtual screen so it cannot stick to /travel.
 */
export function resolveAnalyticsPagePath(
  pathname?: string | null
): string {
  const real = sanitizeAnalyticsPath(
    pathname !== undefined && pathname !== null
      ? pathname
      : typeof window !== 'undefined'
        ? window.location.pathname || ''
        : ''
  )
  if (typeof window === 'undefined') return real
  if (real === '/app-beta') {
    return currentWagamourAnalyticsPath ?? real
  }
  currentWagamourAnalyticsPath = null
  return real
}

/** If a caller passed the shell path `/app-beta`, substitute the active screen. */
export function remapAppBetaShellPagePath(path: string): string {
  const sanitized = sanitizeAnalyticsPath(path)
  if (typeof window === 'undefined') return sanitized
  if (sanitized === '/app-beta' && currentWagamourAnalyticsPath) {
    return currentWagamourAnalyticsPath
  }
  return sanitized
}

export function remapAppBetaShellPageLocation(href: string): string {
  const sanitized = sanitizeAnalyticsLocation(href)
  if (typeof window === 'undefined') return sanitized
  if (!currentWagamourAnalyticsPath) return sanitized
  try {
    const url = new URL(sanitized)
    if (sanitizeAnalyticsPath(url.pathname) === '/app-beta') {
      return sanitizeAnalyticsLocation(
        `${url.origin}${currentWagamourAnalyticsPath}`
      )
    }
  } catch {
    if (sanitized === '/app-beta') return currentWagamourAnalyticsPath
  }
  return sanitized
}

export function isWagamourV2Screen(value: string): value is WagamourV2Screen {
  return V2_SCREEN_SET.has(value)
}

/** Test-only: drop module-level screen so cases do not leak. */
export function __resetWagamourAnalyticsPathForTests(): void {
  currentWagamourAnalyticsPath = null
}
